<template>
  <div class="mt-1">
    <UIError v-if="error" :message="error" />
    <BaseForm
      :fields="fields"
      :initial-data="initialData"
      :submit-label="$t('auth.forms.signIn')"
      @submit="handleSubmit"
      ref="formRef"
    >
      <template #fields>
        <div class="flex items-center justify-end w-full mb-6">
          <div class="text-sm">
            <router-link
              to="/forgot-password"
              class="font-semibold text-gray-400 dark:text-primary-accent hover:text-primary-accent dark:hover:text-primary-accent"
            >
              {{ $t('auth.login.forgotPassword') }}
            </router-link>
          </div>
        </div>
      </template>
    </BaseForm>
  </div>
</template>

<script setup lang="ts">
import { logger, createLogger } from '@/utils/logger'

// Create a namespaced logger
const fileLogger = createLogger('LoginForm')

import { ref, reactive } from 'vue'
import { useAuthStore } from '@/stores/auth'
import BaseForm from './BaseForm.vue'
import UIError from '@/components/ui/UIError.vue'
import type { IFormField } from '@/types/form'
import { IApiError } from '@/types'
import { toast } from '@/utils/toast'
import { useI18n } from 'vue-i18n'

const authStore = useAuthStore()
const error = ref('')
const formRef = ref()
const { t } = useI18n()

const props = defineProps({
  isPopup: {
    type: Boolean,
    default: false
  }
})

const form = reactive({
  email: '',
  password: '',
  rememberMe: false
})

const fields: IFormField[] = [
  {
    name: 'email',
    label: t('auth.forms.email'),
    type: 'email',
    required: true
  },
  {
    name: 'password',
    label: t('auth.forms.password'),
    type: 'password',
    required: true
  }
]

const initialData = {
  email: '',
  password: ''
}

const emit = defineEmits(['success', 'error'])

const handleSubmit = async (formData: any) => {
  error.value = ''
  
  try {
    fileLogger.log(`[LoginForm] Attempting login ${props.isPopup ? 'in popup mode' : 'in regular mode'}`)
    
    await authStore.login({
      email: formData.email,
      password: formData.password,
      remember: form.rememberMe
    }, props.isPopup)
    
    emit('success')
  } catch (e) {
    fileLogger.log(`[LoginForm] Login failed ${props.isPopup ? 'in popup mode' : 'in regular mode'}`, e)
    
    if (e instanceof IApiError) {
      error.value = e.message
      formRef.value?.setErrors(e.errors)
      toast.error({
        title: 'Login Failed',
        message: e.message || 'Invalid email or password'
      })
      emit('error', e)
    } else if (e instanceof Error) {
      error.value = e.message
      formRef.value?.setErrors({ general: e.message })
      toast.error({
        title: 'Login Failed',
        message: e.message
      })
      emit('error', e)
    }
    
    return false
  }
}
</script>